// src/components/FilterBar/FilterBar.tsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Form, Button, Dropdown } from 'react-bootstrap';
import { FaMagnifyingGlass, FaFilter, FaUpload } from 'react-icons/fa6';
import CreateInvoice from '../CreateInvoice/CreateInvoice';
import DateFilter from './components/DateFilter/DateFilter';
import './FilterBar.scss';
import { InvoiceFilters, InvoiceRecord } from '../../types';

interface FilterBarProps {
  invoices: InvoiceRecord[];
  filters: InvoiceFilters;
  onFilterChange: (f: InvoiceFilters) => void;
  statusCounts: { pending: number; approved: number; rejected: number };
  onShowFilters?: () => void;
}

// --- Extracted out to top-level so it doesn't get re-defined on every render ---
interface FilterControlsProps {
  filters: InvoiceFilters;
  onFilterChange: (field: keyof InvoiceFilters, value: any) => void;
  providersList: { id: number; name: string }[];
  searchInputRef: React.RefObject<HTMLInputElement>;
  statusCounts: { pending: number; approved: number; rejected: number };
}

const FilterControls: React.FC<FilterControlsProps> = ({
  filters,
  onFilterChange,
  providersList,
  searchInputRef,
  statusCounts,
}) => (
  <div className="filter-controls-grid">
    {/* Search Bar */}
    <div className="filter-control search-control">
      <div className="control-input search-input">
        <FaMagnifyingGlass className="input-icon" />
        <Form.Control
          ref={searchInputRef}
          placeholder="Facturas, proveedores, totales..."
          value={filters.searchText}
          onChange={e => onFilterChange('searchText', e.target.value)}
        />
      </div>
    </div>

    {/* Status Filter */}
    <div className="filter-control status-control">
      <Dropdown className="control-dropdown">
        <Dropdown.Toggle variant="none" className="dropdown-trigger">
          <div className="trigger-content">
            {filters.status === 'pending' && (
              <>
                <span className="status-text">Pendientes</span>
                <span className="status-count">{statusCounts.pending}</span>
              </>
            )}
            {filters.status === 'approved' && (
              <>
                <span className="status-text">Aprobadas</span>
                <span className="status-count">{statusCounts.approved}</span>
              </>
            )}
            {filters.status === 'rejected' && (
              <>
                <span className="status-text">Rechazadas</span>
                <span className="status-count">{statusCounts.rejected}</span>
              </>
            )}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu className="status-menu">
          <Dropdown.Item
            className={`status-item ${filters.status === 'pending' ? 'active' : ''}`}
            onClick={() => onFilterChange('status', 'pending')}
          >
            <span>Pendientes</span>
            <span className="item-count">{statusCounts.pending}</span>
          </Dropdown.Item>
          <Dropdown.Item
            className={`status-item ${filters.status === 'approved' ? 'active' : ''}`}
            onClick={() => onFilterChange('status', 'approved')}
          >
            <span>Aprobadas</span>
            <span className="item-count">{statusCounts.approved}</span>
          </Dropdown.Item>
          <Dropdown.Item
            className={`status-item ${filters.status === 'rejected' ? 'active' : ''}`}
            onClick={() => onFilterChange('status', 'rejected')}
          >
            <span>Rechazadas</span>
            <span className="item-count">{statusCounts.rejected}</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>

    {/* Date Range Filter */}
    <div className="filter-control date-control">
      <DateFilter
        startDate={filters.startDate}
        setStartDate={(date) => onFilterChange('startDate', date)}
        endDate={filters.endDate}
        setEndDate={(date) => onFilterChange('endDate', date)}
      />
    </div>

    {/* Provider Filter */}
    <div className="filter-control provider-control">
      <Dropdown className="control-dropdown">
        <Dropdown.Toggle variant="none" className="dropdown-trigger">
          <div className="trigger-content">
            <span className="provider-text">
              {providersList.find(p => p.id === filters.providerId)?.name || 'Todos los proveedores'}
            </span>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu className="provider-menu">
          {providersList.map(p => (
            <Dropdown.Item
              key={p.id}
              className={`provider-item ${p.id === filters.providerId ? 'active' : ''}`}
              onClick={() => onFilterChange('providerId', p.id)}
            >
              <span>{p.name}</span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  </div>
);

const FilterBar: React.FC<FilterBarProps> = ({
  invoices,
  filters,
  onFilterChange,
  statusCounts,
  onShowFilters,
}) => {
  const [showCreate, setShowCreate] = useState(false);

  // Keep a ref to the search box
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Whenever `filters.searchText` changes, re-focus the input
  useEffect(() => {
    searchInputRef.current?.focus();
  }, [filters.searchText]);

  // Build unique provider list
  const providersList = useMemo(() => {
    const map = new Map<number, string>();
    invoices.forEach(i => {
      if (i.provider_id != null && i.provider_name) {
        map.set(i.provider_id, i.provider_name);
      }
    });
    return [
      { id: 0, name: 'Todos' },
      ...Array.from(map.entries()).map(([id, name]) => ({ id, name })),
    ];
  }, [invoices]);

  // wrapper to match the prop signature
  const handleFilterChange = (field: keyof InvoiceFilters, value: any) =>
    onFilterChange({ ...filters, [field]: value });

  return (
    <div className="filter-bar-component">
      {/* Main Filter Container */}
      <div className="filter-container">
        <div className="desktop-filters">
          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            providersList={providersList}
            searchInputRef={searchInputRef}
            statusCounts={statusCounts}
          />
        </div>
        
        {/* Mobile Filter Button */}
        <Button
          variant="outline"
          className="mobile-filter-btn"
          onClick={onShowFilters}
        >
          <FaFilter />
        </Button>
      </div>

      {/* FAB */}
      <Button
        className="create-invoice-fab"
        onClick={() => setShowCreate(true)}
      >
        <FaUpload />
        <span className="fab-text">Subir Factura</span>
      </Button>

      {/* CreateInvoice modal */}
      <CreateInvoice show={showCreate} handleClose={() => setShowCreate(false)} />
    </div>
  );
};

export default FilterBar;
